import styled from "styled-components";
import { useState } from "react";
import { Check, X, Undo2, AlertTriangle } from "lucide-react";

import { theme } from "./theme";
import { useT } from "./i18n";

/* Deciding what happens to money collected for something that will not be delivered.
 *
 * Three things have to be visible before the operator commits, because none of them can be undone:
 * how much goes back, how much the property keeps and where that shows up, and the exact address the
 * claim link will be emailed to. The last one is the reason this dialog exists at all — Xendit sends
 * the link to whatever address it is given, so the address IS the delivery.
 */
const Backdrop = styled.div`
  align-items: center;
  background: rgba(15, 23, 42, 0.6);
  display: flex;
  inset: 0;
  justify-content: center;
  padding: 1rem;
  position: fixed;
  z-index: 1600;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  display: flex;
  flex-direction: column;
  max-height: 92vh;
  max-width: 520px;
  width: 100%;
`;

const Head = styled.div`
  align-items: center;
  border-bottom: 1px solid ${theme.border};
  display: flex;
  gap: 0.45rem;
  padding: 0.85rem 1.1rem;
`;

const Title = styled.h3`
  align-items: center;
  color: ${theme.ink};
  display: flex;
  flex: 1 1 auto;
  font-size: 1rem;
  font-weight: 800;
  gap: 0.4rem;
  margin: 0;
`;

const Close = styled.button`
  background: transparent;
  border: 0;
  color: ${theme.ink700};
  cursor: pointer;
  padding: 0.1rem;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  overflow-y: auto;
  padding: 0.85rem 1.1rem;
`;

const Item = styled.div`
  background: ${theme.canvas};
  border: 1px solid ${theme.border};
  border-radius: 10px;
  padding: 0.55rem 0.7rem;
`;

const ItemName = styled.div`
  color: ${theme.ink};
  font-size: 0.9rem;
  font-weight: 800;
`;

const ItemMeta = styled.div`
  color: ${theme.ink700};
  font-size: 0.76rem;
  margin-top: 0.1rem;
`;

const Modes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
`;

const Mode = styled.button`
  background: ${(props) => (props.$on ? theme.gold : "#ffffff")};
  border: 1px solid ${(props) => (props.$on ? theme.gold : theme.border)};
  border-radius: 8px;
  color: ${(props) => (props.$on ? "#ffffff" : theme.ink700)};
  cursor: pointer;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 0.35rem 0.7rem;
`;

const Label = styled.label`
  color: ${theme.ink700};
  display: block;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  margin-bottom: 0.2rem;
  text-transform: uppercase;
`;

const Input = styled.input`
  border: 1px solid ${theme.border};
  border-radius: 8px;
  font-size: 0.85rem;
  padding: 0.4rem 0.55rem;
  width: 100%;
`;

const Split = styled.div`
  background: ${theme.goldSoft};
  border: 1px solid ${theme.gold};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.6rem 0.7rem;
`;

const SplitRow = styled.div`
  color: ${theme.ink};
  display: flex;
  font-size: 0.85rem;
  justify-content: space-between;

  strong {
    font-weight: 800;
  }
`;

const Hint = styled.div`
  color: ${theme.ink700};
  font-size: 0.76rem;
`;

const Toggle = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  color: ${theme.gold};
  cursor: pointer;
  display: flex;
  font-size: 0.79rem;
  font-weight: 800;
  gap: 0.3rem;
  padding: 0;
`;

const Warn = styled.div`
  align-items: flex-start;
  background: #fff6e5;
  border: 1px solid #f0c36d;
  border-radius: 8px;
  color: #8a5a00;
  display: flex;
  font-size: 0.78rem;
  gap: 0.35rem;
  padding: 0.45rem 0.55rem;
`;

const Foot = styled.div`
  border-top: 1px solid ${theme.border};
  display: flex;
  gap: 0.45rem;
  justify-content: flex-end;
  padding: 0.7rem 1.1rem;
`;

const Btn = styled.button`
  align-items: center;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  font-size: 0.82rem;
  font-weight: 800;
  gap: 0.3rem;
  padding: 0.5rem 0.95rem;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Ghost = styled(Btn)`
  background: #ffffff;
  border: 1px solid ${theme.border};
  color: ${theme.ink700};
`;

const Ok = styled(Btn)`
  background: ${theme.gold};
  border: 0;
  color: #ffffff;

  &:hover:not(:disabled) {
    background: ${theme.goldDark};
  }
`;

const rupiah = (value) => `Rp${Number(value || 0).toLocaleString("en-US")}`;

// props: { item, guest, busy, onSubmit, onCancel }
const FoRefundDialog = (props) => {
  const { t } = useT();
  const item = props.item || {};
  const guest = props.guest || {};
  const gross = Number(item.gross_rupiah || 0);
  const online = item.channel === "online";

  const [mode, setMode] = useState("none");
  const [percent, setPercent] = useState("0");
  const [amount, setAmount] = useState(String(gross));
  const [note, setNote] = useState("");
  const [overriding, setOverriding] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [reason, setReason] = useState("");

  /* A typed amount above the gross is refused rather than clamped.
   *
   * Clamping is what this did before, and it made the dialog lie: type 500.000 against a 100.000
   * deposit and the split read "return Rp100.000, keep Rp0" — a legitimate-looking decision nobody
   * chose. The operator pressed save and met a server error instead, having already been shown a
   * number that said it was fine. The backend refuses it either way (FoRefunds::Decision), so the
   * only question is whether the desk finds out before or after pressing the button.
   */
  const typedAmount = Math.round(Number(amount));
  const amountBlank = mode === "fixed" && String(amount).trim() === "";
  const amountTooHigh =
    mode === "fixed" && Number.isFinite(typedAmount) && typedAmount > gross;
  const amountInvalid = amountBlank || amountTooHigh;

  const back = () => {
    if (mode === "percent") {
      const deduction = Math.min(100, Math.max(0, Number(percent) || 0));
      return Math.round((gross * (100 - deduction)) / 100);
    }
    if (mode === "fixed") {
      return Math.max(0, Number.isFinite(typedAmount) ? typedAmount : 0);
    }
    return gross;
  };

  const refundRupiah = back();
  const keptRupiah = gross - refundRupiah;
  const destination = email || (online ? guest.email : guest.phone) || "";
  const missingDestination = online && refundRupiah > 0 && !destination;

  const submit = () =>
    props.onSubmit({
      subject_type: item.subject_type,
      subject_id: item.subject_id,
      reduction_kind: mode,
      reduction_percent: mode === "percent" ? percent : null,
      final_amount: mode === "fixed" ? amount : null,
      note: note,
      override_email: overriding ? email : null,
      override_phone: overriding ? phone : null,
      override_reason: overriding ? reason : null,
    });

  return (
    <Backdrop onClick={props.onCancel}>
      <Card onClick={(event) => event.stopPropagation()}>
        <Head>
          <Title>
            <Undo2 size={16} />
            {t("fo_refund_title")}
          </Title>
          <Close type="button" aria-label={t("close")} onClick={props.onCancel}>
            <X size={18} />
          </Close>
        </Head>

        <Body>
          <Item>
            <ItemName>{item.label}</ItemName>
            <ItemMeta>
              {item.detail ? `${item.detail} · ` : ""}
              {item.gross_formatted} ·{" "}
              {online ? t("fo_refund_ch_online") : t("fo_refund_ch_cash")}
            </ItemMeta>
          </Item>

          <div>
            <Label>{t("fo_refund_decision")}</Label>
            <Modes>
              <Mode
                type="button"
                $on={mode === "none"}
                onClick={() => setMode("none")}
              >
                {t("fo_refund_mode_full")}
              </Mode>
              <Mode
                type="button"
                $on={mode === "percent"}
                onClick={() => setMode("percent")}
              >
                {t("fo_refund_mode_percent")}
              </Mode>
              <Mode
                type="button"
                $on={mode === "fixed"}
                onClick={() => setMode("fixed")}
              >
                {t("fo_refund_mode_fixed")}
              </Mode>
            </Modes>
          </div>

          {mode === "percent" ? (
            <div>
              <Label htmlFor="fo-refund-percent">
                {t("fo_refund_percent_label")}
              </Label>
              <Input
                id="fo-refund-percent"
                type="number"
                min="0"
                max="100"
                value={percent}
                onChange={(event) => setPercent(event.target.value)}
              />
            </div>
          ) : null}

          {mode === "fixed" ? (
            <div>
              <Label htmlFor="fo-refund-amount">
                {t("fo_refund_amount_label")}
              </Label>
              <Input
                id="fo-refund-amount"
                type="number"
                min="0"
                max={gross}
                value={amount}
                aria-invalid={amountInvalid}
                aria-describedby={
                  amountInvalid ? "fo-refund-amount-error" : undefined
                }
                onChange={(event) => setAmount(event.target.value)}
              />
              {amountInvalid ? (
                <Warn id="fo-refund-amount-error" role="alert">
                  <AlertTriangle size={14} />
                  <span>
                    {amountTooHigh
                      ? t("fo_refund_amount_max").replace(
                          "%{max}",
                          rupiah(gross),
                        )
                      : t("fo_refund_amount_blank")}
                  </span>
                </Warn>
              ) : null}
            </div>
          ) : null}

          <Split>
            <SplitRow>
              <span>{t("fo_refund_back")}</span>
              <strong>{rupiah(refundRupiah)}</strong>
            </SplitRow>
            <SplitRow>
              <span>{t("fo_refund_kept")}</span>
              <strong>{rupiah(keptRupiah)}</strong>
            </SplitRow>
            {keptRupiah > 0 ? <Hint>{t("fo_refund_kept_note")}</Hint> : null}
            <Hint>
              {refundRupiah === 0
                ? t("fo_refund_where_none")
                : online
                  ? `${t("fo_refund_where_online")} ${destination || "—"}`
                  : t("fo_refund_where_cash")}
            </Hint>
          </Split>

          <div>
            <Label htmlFor="fo-refund-note">{t("fo_refund_note_label")}</Label>
            <Input
              id="fo-refund-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder={t("fo_refund_note_ph")}
            />
          </div>

          {overriding ? (
            <>
              <Hint>{t("fo_refund_override_hint")}</Hint>
              <div>
                <Label htmlFor="fo-refund-email">
                  {t("fo_refund_email_label")}
                </Label>
                <Input
                  id="fo-refund-email"
                  type="email"
                  value={email}
                  placeholder={guest.email || ""}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="fo-refund-phone">
                  {t("fo_refund_phone_label")}
                </Label>
                <Input
                  id="fo-refund-phone"
                  value={phone}
                  placeholder={guest.phone || ""}
                  onChange={(event) => setPhone(event.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="fo-refund-reason">
                  {t("fo_refund_reason_label")}
                </Label>
                <Input
                  id="fo-refund-reason"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </div>
            </>
          ) : (
            <Toggle type="button" onClick={() => setOverriding(true)}>
              {t("fo_refund_override_toggle")}
            </Toggle>
          )}

          {guest.email_is_ota && online ? (
            <Warn>
              <AlertTriangle size={15} style={{ flex: "0 0 auto" }} />
              <span>{t("fo_refund_ota_email")}</span>
            </Warn>
          ) : null}

          {missingDestination ? (
            <Warn>
              <AlertTriangle size={15} style={{ flex: "0 0 auto" }} />
              <span>{t("fo_refund_no_destination")}</span>
            </Warn>
          ) : null}
        </Body>

        <Foot>
          <Ghost type="button" onClick={props.onCancel}>
            <X size={15} /> {t("confirm_cancel")}
          </Ghost>
          <Ok
            type="button"
            disabled={props.busy || missingDestination || amountInvalid}
            onClick={submit}
          >
            <Check size={15} /> {t("fo_refund_ok")}
          </Ok>
        </Foot>
      </Card>
    </Backdrop>
  );
};

export default FoRefundDialog;
